import {EventEmitter, Injectable} from '@angular/core';
import {IPlatformToolHandler} from '@shared/components/platform-tool/platform-tool-container/platform-tool-component';
import {InvalidateSubject} from '@shared/helpers/invalidate.subject';
import {MagicLinkModel, TabModel} from '@shared/models/platform-tool-models';
import {PlatformCommand} from '@shared/commands/command-base';
import {Subject} from 'rxjs';
import {ActivatedRoute, ActivatedRouteSnapshot, Router} from '@angular/router';
import {DialogService} from '@shared/services/dialog-service';
import {FuseSplashScreenService} from '@barebone-fuse-services/splash-screen.service';
import {CommandService} from '@core/services/command.service';
import {take, takeUntil} from 'rxjs/operators';
import {SnackBarService} from '@core/services/snack-bar.service';
import {
  AddParticipantsCommand, DeleteParticipantsCommand, SyncAssociateEntityCommand, SyncParticipantsCommand,
  SyncProjectEntityToCompanyCommand, UpdateParticipantsCommand
} from '@features/dac6-reporter/commands/dac6-reporter.command';
import {Configuration} from '@shared/models/configuration';
import {IWorkflowCollectionState} from '@features/workflow/store/workflow.reducer';
import {select, Store} from '@ngrx/store';
import {CleanWorkflowDataAction} from '@features/workflow/store/workflow.actions';
import {GetFieldSectionsAction, Dynamic_Grid_Data_Request_Parameters} from '@shared/store/actions/virtual-grid.actions';
import {ParticleCacheService} from '@core/services/particle-cache.service';
import {BasketProvider} from '@shared/models/basket-provider';
import {ParticleConfig} from '@features/particle-config';
import {UpdateFieldDefinitionCommand} from '@shared/commands/update-field-definition.command';
import {SetActivityFeedAction, SetCurrentHelpNoteAction} from '@shared/components/platform-sidebar/store/platform-sidebar.action';
import {PlatformSidebarState} from '@shared/components/platform-sidebar/store/platform-sidebar.reducer';
import { PlatformSidebarService } from '@shared/components/platform-sidebar/store/platform-sidebar.service';


@Injectable({
  providedIn: 'root',
})
export class PlatformToolParticipantsHandler implements IPlatformToolHandler {
  invalidateSubject: InvalidateSubject;
  magicLinkModel: MagicLinkModel;
  activatedRoute: ActivatedRoute;
  magicLinkModelValueChanged: EventEmitter<any>;
  unSubscribe$ = new Subject<void>();
  metaData = {
    'SyncProjectEntities': true
  };
  private currentParticipantSourceType: string;
  private sourceType: string;
  private fieldHistorySourceType = 'Dac6-Field-History';
  constructor(
    private router: Router,
    private store: Store<IWorkflowCollectionState>,
    private fuseSplashScreenService: FuseSplashScreenService,
    private particleCacheService: ParticleCacheService,
    private snackBarService: SnackBarService,
    private sideBarService: PlatformSidebarService,
    private platformSidebarStore: Store<PlatformSidebarState>,
    private dialogService: DialogService,
    protected commandService: CommandService
  ) {
    this.invalidateSubject = new InvalidateSubject();
  }

  destroy(): void {
    this.unSubscribe$.next();
    this.unSubscribe$.complete();
  }

  executeCommand(command: PlatformCommand) {
    if (command.fieldValues['RedirectUrl']) {
      this.router.navigateByUrl(command.fieldValues['RedirectUrl']);
    } else {
      switch (command.name) {
        case 'AddParticipantsCommand':
          this.Add(command);
          break;
        case 'WorkflowCleanupCommand':
          this.cleanupWorkFlow(command);
          break;
        case 'CreateParticipantsSurveyCommand':
          this.startSurvey(command);
          break;
        case 'SyncProjectEntitiesToParticipantsEntitiesCommand':
          this.refreshProjectEntities(command);
          break;
        default:
          this.executeCommandToApi(command);
          break;
      }
    }
  }

  setSelectedTab(selectedTab: TabModel): void {
    if (this.sourceType !== selectedTab.tabData['SourceType']) {
      this.metaData['SyncProjectEntities'] = true;
    }
    this.sourceType = selectedTab.tabData['SourceType'];
    if (this.magicLinkModel.fieldValues['WorkflowTool'] === 'Export') {
      this.setCaseIdOnGridParams(selectedTab);
      this.handleExport();
    } else if (this.magicLinkModel.fieldValues['WorkflowTool'] === 'Import') {
      this.magicLinkModel.fieldValues['Parameters'] = {
        'ImportSampleExcelTemplateName': this.magicLinkModel.fieldValues['ImportSampleExcelTemplateName'],
        'TOOL_NAME': this.magicLinkModel.fieldValues['TOOL_NAME']
      };
    } else if (this.magicLinkModel.fieldValues['WorkflowTool'] === 'ManageEntities') {
      this.magicLinkModel.fieldValues['Parameters'] = {
        'ImportSampleExcelTemplateName': this.magicLinkModel.fieldValues['ImportSampleExcelTemplateName'],
        'TOOL_NAME': this.magicLinkModel.fieldValues['TOOL_NAME']
      };
    } else {
      this.setCaseIdOnGridParams(selectedTab);
      this.showHelpNoteSidebarIconBasedOnSourceType(selectedTab);
    }

  }

  showHelpNoteSidebarIconBasedOnSourceType(selectedTab) {
    if(selectedTab.tabData['SourceType']){
      this.sideBarService.showSidebarIcon('Details');
      let helpNoteId = '';
      switch(selectedTab.tabData['SourceType']){
        case 'Relevant-Taxpayers':
          helpNoteId = 'dac6-relevant-taxpayers';
          break;
        case 'Intermediaries':
          helpNoteId = 'dac6-intermediaries';
          break;
        case 'Other-Persons':
          helpNoteId = 'dac6-other-persons';
          break;
      }
      const helpNoteObj = this.getHelpNoteObject(helpNoteId);
      this.platformSidebarStore.dispatch(new SetCurrentHelpNoteAction(helpNoteObj));
    }
  }

  private getHelpNoteObject(helpNoteId) {
    let helpNoteObj = {};
    helpNoteObj['helpNoteId'] = helpNoteId;
    return helpNoteObj;
  }


  private setCaseIdOnGridParams(selectedTab: TabModel) {
    this.currentParticipantSourceType = selectedTab.tabData['SourceType'];
    const gridConfig = Configuration.getSmartGridConfig(selectedTab.tabData['SourceType']);
    gridConfig.Parameters['CASE_ID'] = '';
    this.particleCacheService.saveUserPreferenceGridSetting(Dynamic_Grid_Data_Request_Parameters,
      selectedTab.tabData['SourceType'], {});
    //
    if (this.activatedRoute.snapshot.params['caseId']) {
      gridConfig.Parameters['CASE_ID'] = this.activatedRoute.snapshot.params['caseId'];
      this.particleCacheService.saveUserPreferenceGridSetting(Dynamic_Grid_Data_Request_Parameters,
        selectedTab.tabData['SourceType'], {'CASE_ID': this.activatedRoute.snapshot.params['caseId']});
    }
  }

  private handleExport() {
    const selectedSourceConfig = Configuration.getSmartGridConfig(this.magicLinkModel.fieldValues['GenericParticipantsExportKey'] + '-SELECTED');
    const notSelectedSourceConfig = Configuration.getSmartGridConfig(this.magicLinkModel.fieldValues['GenericParticipantsExportKey'] + '-NOT-SELECTED');
    const fields = Configuration.getSmartGridConfig(this.magicLinkModel.fieldValues['GenericFieldsKey']);

    selectedSourceConfig.Parameters['CASE_ID'] = '';
    notSelectedSourceConfig.Parameters['CASE_ID'] = '';
    selectedSourceConfig.Parameters['SOURCE_TYPE'] = this.magicLinkModel.fieldValues['SourceType'];
    selectedSourceConfig.FilterConfig['ItemType'] = this.magicLinkModel.fieldValues['SourceType'];
    notSelectedSourceConfig.Parameters['SOURCE_TYPE'] = this.magicLinkModel.fieldValues['SourceType'];
    notSelectedSourceConfig.FilterConfig['ItemType'] = this.magicLinkModel.fieldValues['SourceType'];
    // TODO: need attention, it's SourceType inside core business :(
    fields.Parameters['SourceType'] = this.magicLinkModel.fieldValues['SourceType'];
    //
    if (this.magicLinkModel.fieldValues['caseId']) {
      selectedSourceConfig.Parameters['CASE_ID'] = this.magicLinkModel.fieldValues['caseId'];
      notSelectedSourceConfig.Parameters['CASE_ID'] = this.magicLinkModel.fieldValues['caseId'];
    }
    this.magicLinkModel.fieldValues['Parameters'] = {
      'SourceType': this.magicLinkModel.fieldValues['SourceType'],
      'SelectParticipantsTitle': this.magicLinkModel.fieldValues['SelectParticipantsTitle'],
      'ParticipantsFilterTemplateId': this.magicLinkModel.fieldValues['ParticipantsFilterTemplateId']
    };
  }

  valueChanged(event) {
    console.log(event);
    if (event.fieldDefinition && event.fieldDefinition.key === 'CaseId') {
      this.router.navigate(['./detail', event.item.id], {relativeTo: this.activatedRoute});
      return;
    }
    if (event.action) {
      switch (event.action.id) {
        case 'Open':
          this.router.navigate(['detail/', event.item.id], {relativeTo: this.activatedRoute});
          break;
        case 'Edit':
          this.edit(event);
          break;
        case 'Delete':
          const command = new DeleteParticipantsCommand([event.item.id], this.magicLinkModel.fieldValues['DeleteEndpoint']);
          this.fuseSplashScreenService.show();
          this.commandService.executeCommand(command).subscribe(state => {
            if (state) {
              this.fuseSplashScreenService.hide();
              this.invalidateSubject.invalidate();
              this.snackBarService.showInformation('Deleted Successfully');
            }
          });
          break;
        case 'ViewActivity':
          this.trackEntityActiviy(event);
          break;
      }
    }
    console.log(event);
  }
  Add(command: PlatformCommand) {
    this.fuseSplashScreenService.show();
    this.commandService.executeCommand(new AddParticipantsCommand(this.magicLinkModel.fieldValues['AddEndpoint'])).pipe().subscribe(state => {
      this.fuseSplashScreenService.hide();
      if (state && state.Payload) {
        this.snackBarService.showInformation(`${state.Payload.FieldValues.EntityCode} is added`);
        this.invalidateSubject.invalidate();
      }
    });
  }
  private executeCommandToApi(command: PlatformCommand) {
    if (command.api && command.api.length > 0 && !command.isDisabled) {
      this.fuseSplashScreenService.show();
      this.commandService.executePlatformCommand(command).pipe(take(1)).subscribe(commandResponse => {
          console.log('Command Response', commandResponse);
          this.fuseSplashScreenService.hide();
          if (commandResponse.fieldValues['RedirectUrl']) {
            this.router.navigateByUrl(commandResponse.fieldValues['RedirectUrl']);
          } else {
            this.invalidateSubject.invalidate();
          }
        },
        error => {
          console.error('Failed ToolBarComponent command', error);
          this.fuseSplashScreenService.hide();
        });
    }
  }

  private manageEntities(command: PlatformCommand) {
    if (command.api && command.api.length > 0 && !command.isDisabled) {
      this.fuseSplashScreenService.show();
      this.commandService.executePlatformCommand(command).pipe(take(1)).subscribe(commandResponse => {
          console.log('Command Response', commandResponse);
          //
          const syncCommand = new SyncProjectEntityToCompanyCommand();
          this.commandService.executeCommand(syncCommand).pipe().subscribe(state => {
            this.fuseSplashScreenService.hide();
            this.navigateToManageEntities(commandResponse);
          });
        },
        error => {
          console.error('Failed ToolBarComponent command', error);
          this.fuseSplashScreenService.hide();
        });
    }
  }
  private navigateToManageEntities(commandResponse: any) {
    if (commandResponse.fieldValues['RedirectUrl']) {
      this.router.navigateByUrl(commandResponse.fieldValues['RedirectUrl']);
    } else {
      this.invalidateSubject.invalidate();
    }
  }

  private cleanupWorkFlow(command: PlatformCommand) {
    if (this.magicLinkModel.fieldValues['WorkflowTool'] === 'Export') {
      this.store.dispatch(new CleanWorkflowDataAction(command.fieldValues['SelectedTab'].tabData['WorkFlowId']));
      this.router.navigateByUrl(command.fieldValues['SelectedTab'].tabData['ReRouteUrl']);
    }
    if (this.magicLinkModel.fieldValues['WorkflowTool'] === 'Import') {
      this.router.navigateByUrl(command.fieldValues['SelectedTab'].tabData['ReRouteUrl']);
    }
    if (this.magicLinkModel.fieldValues['WorkflowTool'] === 'ManageEntities') {
      this.cleanupManageEntitiesWorkflow(command);
    }
  }

  private cleanupManageEntitiesWorkflow(command: PlatformCommand) {
    this.metaData['SyncProjectEntities'] = false;
    if (command.fieldValues['Status'] === 'Finished' && !this.magicLinkModel.fieldValues['commandExecuted']) {
      this.magicLinkModel.fieldValues['commandExecuted'] = true;
      const workflowState = command.fieldValues['WorkflowState'];
      const manageEntitiesState = workflowState.States.find(s => s.Id === 'manage-entities');
      let filterId;
      if (manageEntitiesState) {
        const filterIdInput = manageEntitiesState.Inputs.find(i => i.Name === 'FilterId');
        filterId = filterIdInput ? filterIdInput.Value : null;
      }
      const syncCommand = new SyncParticipantsCommand(filterId, this.magicLinkModel.fieldValues['SyncEntityEndpoint']);
      this.fuseSplashScreenService.show();
      this.commandService.executeCommand(syncCommand).pipe().subscribe(commandState => {
        this.fuseSplashScreenService.hide();
        if (commandState) {
          this.store.dispatch(new GetFieldSectionsAction(command.fieldValues['SourceType']));
          this.router.navigateByUrl(command.fieldValues['SelectedTab'].tabData['ReRouteUrl']);
        }
      }, error => {
        console.log(error);
        this.fuseSplashScreenService.hide();
        return this.router.navigateByUrl(command.fieldValues['SelectedTab'].tabData['ReRouteUrl'])
      });
    }
    if (command.fieldValues['Status'] === 'Cancelled') {
      this.fuseSplashScreenService.hide();
      this.router.navigateByUrl(command.fieldValues['SelectedTab'].tabData['ReRouteUrl']);
    }
  }

  private startSurvey(command: PlatformCommand) {
    const selectedBasket = BasketProvider.getCurrentBasket();
    const { flag } = selectedBasket;
    const bucket = flag === 'MX' ? '-MX' : '';
    const title = `${this.currentParticipantSourceType}${bucket}`;
    const parameters = {'SourceTypes': [`Relevant-Taxpayers${bucket}`, `Relevant-Taxpayers${bucket}`, `Intermediaries${bucket}`, `Other-Persons${bucket}`]};
    this.dialogService.createOrLoadDefaultSurveyTemplate(title, false, false, null, parameters);
  }
  edit(event: any) {
    this.openEditor(event);
  }

  public openEditor(event: any) {
    let minWidth = '400px';
    let maxWidth = '1500px';
    if (event.fieldDefinition.key === 'RtpAssociateEnterprise') {
      minWidth = '900px';
      maxWidth = '900px';
    }
    this.dialogService.showCellEditorDialog(event.item, event.fieldDefinition, minWidth, maxWidth, this.sourceType, this.fieldHistorySourceType)
      .subscribe(state => {
        console.log('After Edit', state);
        if (state.action === 'OK') {
          if (event.fieldDefinition.key === 'RtpAssociateEnterprise') {
            const syncAssociateEntityCommand = new SyncAssociateEntityCommand(event.item.id);
            this.commandService.executeCommand(syncAssociateEntityCommand).subscribe(commandState => {
              console.log('Sync Success');
            });
            this.invalidateSubject.invalidate();
            return;
          }
          const item = {id: event.item.id, fieldValues: {}};
          const value = event.item.cells[event.fieldDefinition.key].value;
          item.fieldValues[event.fieldDefinition.key] = value;
          console.log(item);
          const command = new UpdateParticipantsCommand(item, this.magicLinkModel.fieldValues['UpdateEndpoint']);
          this.commandService.executeCommand(command).pipe().subscribe(commandState => {
            console.log(commandState);
            this.invalidateSubject.invalidate();
          });
        } else if (state.action === 'Cancel') {
          if (event.fieldDefinition.key === 'RtpAssociateEnterprise') {
            const command = new SyncAssociateEntityCommand(event.item.id);
            this.commandService.executeCommand(command).subscribe(commandState => {
              console.log('Sync Success');
            });
            this.invalidateSubject.invalidate();
            return;
          }
        }
      });
  }
  saveFieldDefinition(fieldDefinition: any, selectedFieldSection: any) {
    let fieldSettings = null;
    if (fieldDefinition && fieldDefinition.configKey) {
      const fieldConfig = Configuration.getSmartGridConfig(fieldDefinition.configKey);
      if (fieldConfig && fieldConfig.FieldSettings) {
        fieldSettings = fieldConfig.FieldSettings;
      }
    }
    if (fieldSettings) {
      const rootUrlKey = Configuration.getSmartGridConfig(this.sourceType).RootUrlKey;
      const url = `${Configuration.getConfig(rootUrlKey)}FieldSection/UpdateFieldDefinition`;
      const config = Configuration.getSmartGridConfig(this.sourceType);
      if (config) {
        const sourceType = config.SourceType;
        const updateFieldDefinitionCommand = new UpdateFieldDefinitionCommand([fieldDefinition], selectedFieldSection.id, ParticleConfig.ApplicationId, sourceType, true,
          url, !Configuration.getSmartGridConfig(this.sourceType).IsCompanySpecific);
        this.commandService.executeCommand(updateFieldDefinitionCommand).pipe().subscribe(commandState => {
          this.invalidateSubject.invalidate();
        });
      }
    }
  }
  private trackEntityActiviy(event: any) {
    this.platformSidebarStore.dispatch(new SetActivityFeedAction({type: 'ReferenceCode', id: event.item.cells['ReferenceCode'].value, title: event.item.cells['EntityCode'].value, isDefault: false, isUserAction: true}));
  }

  refreshProjectEntities(command: PlatformCommand) {
    this.dialogService.showYesNoDialog('Refresh Entities', `The entities in your ${command.fieldValues['ParticipantsSourceType']} will be refreshed with the entities of your current loaded project. This will bring in any changes you have made to your entities in your current project. <br /> Do you wish to sync your entities with Project Settings Entity Data?`)
      .subscribe(state => {
        if (state.action === 'OK') {
          this.commandService.executePlatformCommand(command).pipe().subscribe(commandState => {
            console.log(commandState);
            this.invalidateSubject.invalidate();
          });
        }
      });
  }
}
